import type {Asset} from '@hconnect/common'
import {dataTestId} from '@hconnect/uikit'
import {Alert} from '@hconnect/uikit/src/lib2'
import {Box, Checkbox, List, ListItem, ListItemIcon, Stack, Typography} from '@mui/material'
import React, {FC, useMemo, useState} from 'react'
import {Controller, UseFormReturn} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {GrindingCircuitPayload} from '../../types'

import {BaseTextField, ControlledTextField} from '@settings/modules/common/components'
import {requiredValidator} from '@settings/modules/common/utils'

export type GrindingCircuitsFormFieldsProps = {assets: Asset[]} & Pick<
  UseFormReturn<GrindingCircuitPayload>,
  'control'
>

export const GrindingCircuitsFormFields: FC<GrindingCircuitsFormFieldsProps> = ({
  control,
  assets
}) => {
  const {t} = useTranslation()
  const [search, setSearch] = useState<string>('')

  const filteredAssets = useMemo(() => {
    const searchLower = search.toLowerCase()
    return assets.filter((asset) => asset.name.toLowerCase().includes(searchLower))
  }, [search, assets])

  return (
    <>
      <ControlledTextField<GrindingCircuitPayload>
        formDataName="name"
        label={t('plantSetup.grindingCircuit.label.name')}
        control={control}
        rules={requiredValidator(t)}
        {...dataTestId('grinding_circuit_name_field')}
      />
      <Stack spacing={2} overflow="auto">
        <BaseTextField
          label={t('plantSetup.grindingCircuit.label.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          {...dataTestId('grinding_circuit_asset_search')}
        />
        <Controller
          name="assetIds"
          control={control}
          rules={requiredValidator(t)}
          render={({field: {value = [], onChange}, fieldState: {error}}) => (
            <>
              <Typography color="grey.700" mb={2}>
                {t('plantSetup.grindingCircuit.label.selectCementMill')}
              </Typography>
              <List dense disablePadding sx={{maxHeight: 380, overflowY: 'auto'}}>
                {filteredAssets.map((asset, index) => (
                  <ListItem
                    key={asset.id}
                    dense
                    disableGutters
                    divider={index !== filteredAssets.length - 1}
                  >
                    <ListItemIcon sx={{minWidth: 'unset', ml: 2}}>
                      <Checkbox
                        edge="start"
                        tabIndex={-1}
                        disableRipple
                        checked={value.includes(asset.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onChange([...value, asset.id])
                          } else {
                            onChange(value.filter((id) => id !== asset.id))
                          }
                        }}
                        {...dataTestId(`grinding_circuit_asset_checkbox_${asset.id}`)}
                      />
                    </ListItemIcon>
                    <Typography>{asset.name}</Typography>
                  </ListItem>
                ))}
                {filteredAssets.length === 0 && (
                  <Typography variant="body2" color="textSecondary" sx={{px: 2}}>
                    {t('plantSetup.grindingCircuit.label.noMatchingAssets')}
                  </Typography>
                )}
                {error && (
                  <Box mt={2} mb={1}>
                    <Alert
                      accentColor="error"
                      content={t('plantSetup.grindingCircuit.label.cementMillRequired')}
                      {...dataTestId('grinding_circuit_no_cement_mills_selected_error')}
                    />
                  </Box>
                )}
              </List>
            </>
          )}
        />
      </Stack>
    </>
  )
}
